/**
 * The card that came with the set.
 *
 * A first-time player has to learn four things — the dial is coarse and fine, a signal has to be
 * *held* rather than swept past, the set keeps a log, and you can answer. A modal with arrows
 * would teach all four in fifteen seconds and cost the thing the game is actually selling, which
 * is the feeling of having been handed a piece of equipment and left alone with it.
 *
 * So the tutorial is an object in the world: an index card taped to the receiver by whoever set
 * it up, written in their voice. It teaches by telling the player what the set does, in the order
 * they will need it — and it advances only when they have *actually done the thing*, never on a
 * "next" button. A player who already knows how a radio works will complete the whole card
 * without reading it, which is the point: the reward for competence is that the tutorial never
 * happens.
 *
 * ## Why the steps are predicates over game state
 *
 * Nothing here observes clicks. Each step asks a question about the world — has the dial moved
 * far enough to have been *used*, is there anything in the log, has a conversation been opened —
 * so the card cannot be fooled by wiggling a control, and cannot get stuck if the player does
 * things out of order or in another tab. It also means every step is a pure function of a
 * snapshot, which is why this file has no DOM in it and can be tested on its own.
 */

/** What the card can see. Everything is derived from the save or from the receiver. */
export interface OnboardingSnapshot {
  /** The set is on. Nothing is taught while it is off — there is nothing to teach yet. */
  powered: boolean;
  /** Total distance the dial has been moved this session, in kHz. */
  dialTravelKhz: number;
  /** Entries in the frequency log: one per signal actually held long enough to register. */
  logged: number;
  /**
   * The player has worked the case notes themselves — changed tab, or re-tuned from a log entry.
   *
   * Not "the panel is visible": it always is, and it comes forward on its own when a clue lands.
   * The step is about the skill (the log is navigable), so it has to be about doing something.
   */
  notesUsed: boolean;
  /** A conversation has been opened at least once. */
  spoken: boolean;
}

export type OnboardingStepId = 'tune' | 'hold' | 'log' | 'answer' | 'done';

export interface OnboardingStep {
  id: OnboardingStepId;
  /** Locale key for the card's line. */
  key: string;
  /** True once the player has done this for real. */
  done: (snapshot: OnboardingSnapshot) => boolean;
}

/**
 * How far the dial must travel before tuning counts as learned.
 *
 * A nudge is an accident; 400 kHz is a deliberate sweep across a good part of the band. Set it
 * much lower and the card advances while the player is still working out what the control is.
 */
export const TUNE_TRAVEL_KHZ = 400;

/**
 * The steps, in the order a player needs them.
 *
 * Each is a thing the set does, phrased as the person who left it would phrase it — instructions
 * first, and only at the end the sentence that makes the card an artefact rather than a manual.
 */
export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {
    id: 'tune',
    key: 'card.tune',
    done: (s) => s.dialTravelKhz >= TUNE_TRAVEL_KHZ,
  },
  {
    id: 'hold',
    key: 'card.hold',
    // Logging happens only after the dwell timer, so an entry *is* proof of having held one.
    done: (s) => s.logged > 0,
  },
  {
    id: 'log',
    key: 'card.log',
    done: (s) => s.notesUsed,
  },
  {
    id: 'answer',
    key: 'card.answer',
    done: (s) => s.spoken,
  },
];

export interface OnboardingState {
  /** Which line the card is showing, or 'done' when there is nothing left to teach. */
  step: OnboardingStepId;
  /** Position in the sequence, for the card's progress marks. */
  index: number;
  total: number;
  /** Whether the card should be on screen at all. */
  visible: boolean;
}

export interface OnboardingInput {
  snapshot: OnboardingSnapshot;
  /** The player turned the card over. Permanent, and stored in the save. */
  dismissed: boolean;
  /** A returning player is not taught anything. */
  firstRun: boolean;
}

/**
 * What the card should show right now.
 *
 * The first *incomplete* step wins, rather than a stored cursor. That matters more than it
 * sounds: a player who logs a signal before touching the notes has genuinely learned to hold a
 * signal, and a card that insisted on teaching it afterwards would be visibly not paying
 * attention. It also makes the whole thing idempotent — the card cannot drift out of step with
 * the save, because it *is* a view of the save.
 */
export function onboardingState(input: OnboardingInput): OnboardingState {
  const total = ONBOARDING_STEPS.length;
  const index = ONBOARDING_STEPS.findIndex((step) => !step.done(input.snapshot));
  const step = index === -1 ? 'done' : ONBOARDING_STEPS[index]!.id;

  // A finished card does not vanish. It shows its last line and waits to be turned over, which
  // gives the tutorial an ending instead of a disappearance, and leaves the player putting the
  // card down themselves — the one moment where dismissing it is the intended action rather than
  // an escape hatch.
  const visible = input.firstRun && !input.dismissed && input.snapshot.powered;

  return { step, index: index === -1 ? total : index, total, visible };
}

/** Locale key for the line shown once every step is done. */
export const ONBOARDING_DONE_KEY = 'card.done';

/** The locale key for a step id, including the closing beat. */
export function onboardingLineKey(step: OnboardingStepId): string {
  if (step === 'done') return ONBOARDING_DONE_KEY;
  return ONBOARDING_STEPS.find((candidate) => candidate.id === step)?.key ?? ONBOARDING_DONE_KEY;
}
